<script setup lang="ts">
import { computed, onMounted } from "vue"
const route = useRoute()

import TrustindexWidget from "~/components/ui/TrustindexWidget.vue"
import VideoHeader from "~/components/ui/VideoHeader.vue"
import Marquee from "~/components/ui/Marquee.vue"
import LazyThreeColumns from "~/components/ui/ThreeColumns.vue"
import LazyCenterAlignedFeature from "~/components/CenterAlignedFeature.vue"

interface CardItem {
	header?: string
	text?: string
	[key: string]: any
}

const trustindexRef = ref<HTMLElement | null>(null)
const shouldLoadReviews = ref(false)

definePageMeta({
	layout: "default"
})

function decodeHtml(html: string) {
	return html
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#039;/g, "'")
}

const { getLandingPage } = useWordPress()
const { data: page } = await useAsyncData("wp-index", () => getLandingPage("index-page"))

const landing = computed(() => page.value?.acf || {})
const layoutStyle = computed(() => landing.value?.layoutstyle)
const stateLayoutStyle = useLayoutStyle()
watch(
	layoutStyle,
	(val) => {
		stateLayoutStyle.value = val || null
	},
	{ immediate: true }
)
const showReviews = computed(() => {
	const value = landing.value?.show_reviews
	return value === "true" || value === true
})

const videoHeaderVideoUrl = computed(() => landing.value?.video_header_video_url || "")
const videoHeaderPoster = computed(() => landing.value?.video_header_video_poster || "")

const videoHeaderTitle = computed(() => landing.value?.video_header_title || "")
const videoHeaderExcerpt = computed(() => landing.value?.video_header_excerpt || "")
const videoHeaderPrimaryButton = computed(() => landing.value?.video_header_primary_button || "")
const videoHeaderPrimaryButtonUrl = computed(() => landing.value?.video_header_primary_button_url || "")
const videoHeaderSecondaryButton = computed(() => landing.value?.video_header_secondary_button || "")
const videoHeaderSecondaryButtonUrl = computed(() => landing.value?.video_header_secondary_button_url || "")
const videoHeaderTertiaryButton = computed(() => landing.value?.video_header_tertiary_button || "")
const videoHeaderTertiaryButtonUrl = computed(() => landing.value?.video_header_tertiary_button_url || "")

// 'firstcolumnheader', 'secondcolumnheader', etc.
/*const standardCards = computed(() => extractColumns(landing.value, ["first", "second", "third", "fourth"]))*/

// 'header_one', 'header_two', 'header_three'
// 1. Compute your columns as before
const threeColumnItems = computed(() => extractColumns(landing.value, ["one", "two", "three"]))
const featureHeaderSm = computed(() => landing.value?.feature_header_sm || "")
const featureHeaderLg = computed(() => landing.value?.feature_header_lg || "")
const featureImage = computed(() => landing.value?.feature_image || "")
const featureUrl = computed(() => landing.value?.feature_url || "")
const featureButton = computed(() => landing.value?.feature_button || "")
const featureText = computed(() => landing.value?.feature_text || "")
const featureContent = computed(() => landing.value?.feature_content || "")

const businessLoungeHeaderSm = computed(() => landing.value?.business_lounge_header_sm)
const businessLoungeHeader = computed(() => landing.value?.business_lounge_header)
const businessLoungeText = computed(() => landing.value?.business_lounge_text)
const businessLoungeButton = computed(() => landing.value?.business_lounge_button)
const businessLoungeUrl = computed(() => landing.value?.business_lounge_url)

const centeredFeatureHeader = computed(() => landing.value?.centered_feature_header)
const centeredFeatureText = computed(() => landing.value?.centered_feature_text)

const seoTitle = computed(() => {
	return page.value?.yoast_head_json?.title
})

const ogImage = computed(() => {
	return page.value?.yoast_head_json?.og_image?.[0]?.url || "/default-og.jpg"
})

const seoDescription = computed(() => {
	return page.value?.yoast_head_json?.description
})

useSeoMeta({
	title: seoTitle,
	titleTemplate: null,
	description: seoDescription,
	ogTitle: seoTitle,
	ogUrl: "https://floatinggoat.dk",
	ogDescription: seoDescription,
	ogImage: ogImage,
	ogType: "website",
	twitterCard: "summary_large_image",
	twitterTitle: seoTitle,
	twitterDescription: seoDescription,
	twitterImage: ogImage
})

onMounted(() => {
	const observer = new IntersectionObserver(
		([entry]) => {
			if (entry?.isIntersecting) {
				shouldLoadReviews.value = true
				observer.disconnect()
			}
		},
		{ rootMargin: "200px" }
	)
	if (trustindexRef.value) observer.observe(trustindexRef.value)
})

if (import.meta.dev && import.meta.client) {
	console.log(landing.value)
	console.log(route.params)
}
</script>
<template>
	<NuxtLayout>
		<template v-if="layoutStyle === 'video'" #bg-video>
			<VideoHeader :title="videoHeaderTitle" :excerpt="videoHeaderExcerpt" :videoUrl="videoHeaderVideoUrl" :posterUrl="videoHeaderPoster" :primaryButtonText="videoHeaderPrimaryButton" :primaryButtonUrl="videoHeaderPrimaryButtonUrl" :secondaryButtonText="videoHeaderSecondaryButton" :secondaryButtonUrl="videoHeaderSecondaryButtonUrl" :tertiaryButtonText="videoHeaderTertiaryButton" :tertiaryButtonUrl="videoHeaderTertiaryButtonUrl" />
		</template>
		<LazyCenterAlignedFeature :header="centeredFeatureHeader" :text="centeredFeatureText" />
		<LazyThreeColumns v-if="threeColumnItems.length" :items="threeColumnItems" class="max-w-screen" />
		<LazyMainFeatureSection :feature-header-sm="featureHeaderSm" :feature-header-lg="featureHeaderLg" :feature-image="featureImage" :feature-text="featureText" :feature-button="featureButton" :feature-url="featureUrl" :feature-content="featureContent" />
		<Marquee folder="clients" :speed="40" />
		<div ref="trustindexRef" class="hidden">
			<TrustindexWidget v-if="showReviews === true && shouldLoadReviews" />
		</div>
		<LazyFerryVideo :header="businessLoungeHeader" :header-sm="businessLoungeHeaderSm" :text="businessLoungeText" :url="businessLoungeUrl" :button="businessLoungeButton" />
	</NuxtLayout>
</template>
